import {AnimatePresence, motion} from 'framer-motion'
import {useLinkResolver} from 'prismicio'
import {TestimonialsSectionSlicePartnershipsPrimaryTabItem} from 'prismicio-types'
import {motionImageProps, motionProps} from 'slices/TestimonialsSection/constants'
import {PrismicRichText} from '@prismicio/react'
import Link from 'next/link'
import {EVENTS, track} from 'src/common/helpers'
import {Icon} from 'src/common/Icon'
import {isFilled} from '@prismicio/client'

export const ContentContainer = ({
  activeTab,
  activeTabContent,
  componentIndex,
  pageUid,
}: {
  activeTab: number
  activeTabContent: TestimonialsSectionSlicePartnershipsPrimaryTabItem
  componentIndex: number
  pageUid: string
}) => {
  const {linkResolver} = useLinkResolver()

  return (
    <div className="flex flex-col gap-8 rounded-lg border border-moss-black/[0.1] bg-moss-white p-6 md:p-8 lg:flex-row lg:gap-6">
      <AnimatePresence mode="sync">
        <div className="flex flex-col gap-8 md:w-full md:justify-between">
          <div className="flex flex-col gap-3 md:gap-4">
            <motion.span className="text-button-s text-[#2C8FFF]" key={`activeTab-tag-${activeTab}`} {...motionProps}>
              {activeTabContent.tab_tag}
            </motion.span>
            <motion.h4
              key={`activeTab-title-${activeTab}`}
              {...motionProps}
              className="text-24 font-medium leading-[normal] text-moss-black lg:leading-[30px]"
            >
              {activeTabContent.tab_title}
            </motion.h4>
            <motion.div key={`activeTab-description-${activeTab}`} {...motionProps}>
              <PrismicRichText
                field={activeTabContent.tab_content}
                components={{
                  paragraph: ({children}) => <p className="text-body-m text-moss-black">{children}</p>,
                  list: ({children}) => (
                    <ul className="text-tg-body-m flex list-none flex-col gap-2 text-moss-grey-500">{children}</ul>
                  ),
                  listItem: ({children}) => (
                    <li className="flex items-start gap-3">
                      <div className="mt-[7px] h-[5px] w-[5px] min-w-[5px] rounded-[1px] bg-moss-grey-300" />
                      {children}
                    </li>
                  ),
                }}
              />
            </motion.div>
          </div>
          <Link
            href={linkResolver(activeTabContent.link_url)}
            locale={activeTabContent.link_url.lang || 'en-us'}
            className="group relative flex h-[38px] w-fit items-center gap-[14px] rounded-[4px] border border-moss-grey-150 bg-moss-white pl-[14px] pr-[7px] hover:bg-moss-grey-100"
            onClick={() =>
              track({
                event: EVENTS.CLICK,
                mktgId: `testimonial-${activeTab}-button`,
                location: `component-${componentIndex}-testimonials`,
                pageName: pageUid,
                dataType: 'customer-story-button',
              })
            }
          >
            <span className="text-button-s uppercase text-black">{activeTabContent.link_url.text}</span>
            <div className="flex h-6 w-6 items-center justify-center rounded-sm bg-green-neon group-hover:bg-green-neon/[80%]">
              <Icon name="arrow" className="h-2 w-2.5 text-green-dark" />
            </div>
          </Link>
        </div>
      </AnimatePresence>
      <div className="relative isolate flex aspect-[556/320] w-full items-end overflow-hidden rounded-md p-2">
        <AnimatePresence mode="sync">
          <motion.img
            className="absolute inset-0 -z-10 h-full w-full overflow-hidden object-cover"
            key={`activeTab-image-${activeTab}`}
            src={activeTabContent.image.url || ''}
            alt={activeTabContent.image.alt || ''}
            {...motionImageProps}
          />
          {isFilled.keyText(activeTabContent.quote) && (
            <motion.div
              key={`activeTab-quote-${activeTab}`}
              {...motionProps}
              className="flex w-full items-center gap-4 rounded-[4px] border border-moss-white/[40%] bg-moss-white/[10%] p-3 backdrop-blur-[40px] md:w-fit"
            >
              <div className="flex h-8 min-h-8 w-8 min-w-8 items-center justify-center rounded-[2px] bg-moss-white">
                <Icon name="quotation-mark" className="h-1.5 w-3 text-black" />
              </div>
              <p className="text-15 font-medium leading-[19px] text-moss-white">{activeTabContent.quote}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
